import {FC} from 'react'
import {Clock, User, Armchair, MapPin, Zap} from 'lucide-react'

type Allocation = {
  id: number
  allocatedAt: string
  status: string
  allocationMethod: string
  queue?: {
    id: number
    people: number
    name: string
    phone: string
  }
  table?: {
    id: number
    number: string
    environment: string
    environmentId: number
    capacity: number
  }
  efficiency?: {
    people: number
    totalCapacity: number
    waste: number
    wastePercentage: number
    badge: string
    badgeColor: string
  }
  notes: string
}

type Props = {
  allocations: Allocation[]
  loading?: boolean
}

const AllocationsList: FC<Props> = ({allocations, loading = false}) => {
  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'allocated':
        return '#4377FF' // Azul
      case 'occupied':
        return '#3FB669' // Verde
      case 'cleaning':
        return '#FFA500' // Laranja
      default:
        return '#6E7586' // Cinza
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'allocated':
        return 'Alocada'
      case 'occupied':
        return 'Ocupada'
      case 'cleaning':
        return 'Limpando'
      default:
        return status
    }
  }

  // Agrupar alocações por queueId (mesas unidas aparecem juntas)
  const groupedAllocations = allocations.reduce((acc, allocation) => {
    const key = allocation.queue?.id || `booking-${allocation.id}`
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(allocation)
    return acc
  }, {} as Record<string, Allocation[]>)

  return (
    <div style={{width: '100%'}}>
      <h4
        style={{
          marginBottom: 16,
          color: '#2A2D36',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <MapPin size={20} color='#4377FF' />
        Mesas Alocadas
      </h4>

      {loading ? (
        <div style={{textAlign: 'center', padding: 20}}>
          <p style={{color: '#6E7586'}}>Carregando alocações...</p>
        </div>
      ) : allocations.length === 0 ? (
        <div style={{textAlign: 'center', padding: 20}}>
          <p style={{color: '#6E7586'}}>Nenhuma alocação encontrada neste período</p>
        </div>
      ) : (
        <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
          {Object.entries(groupedAllocations).map(([key, allocationsGroup]) => {
            // Usar primeira alocação do grupo como referência
            const firstAllocation = allocationsGroup[0]
            const allMesas = allocationsGroup.filter((a) => a.table)

            return (
              <div
                key={key}
                style={{
                  border: '1px solid #D3D9E8',
                  borderRadius: 8,
                  padding: 12,
                  backgroundColor: '#FAFBFC',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                {/* Cabeçalho: Horário e Status */}
                <div
                  style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: '#6E7586',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Clock size={14} />
                    {formatTime(firstAllocation.allocatedAt)}
                  </span>
                  <span
                    style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      backgroundColor: getStatusColor(firstAllocation.status),
                      color: 'white',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {getStatusLabel(firstAllocation.status)}
                  </span>
                </div>

                {/* Info do Cliente */}
                {firstAllocation.queue && (
                  <div>
                    <p
                      style={{
                        fontSize: 11,
                        color: '#6E7586',
                        margin: '0 0 4px 0',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <User size={12} />
                      Cliente
                    </p>
                    <p style={{fontSize: 13, color: '#2A2D36', margin: 0, fontWeight: 600}}>
                      {firstAllocation.queue.name}
                    </p>
                    <p style={{fontSize: 11, color: '#6E7586', margin: '2px 0 0 0'}}>
                      {firstAllocation.queue.people}{' '}
                      {firstAllocation.queue.people === 1 ? 'pessoa' : 'pessoas'}
                    </p>
                  </div>
                )}

                {/* Mesas - Mostrar todas unidas se houver mais de uma */}
                {allMesas.length > 0 && (
                  <div>
                    {(() => {
                      const totalCapacity = allMesas.reduce(
                        (sum, mesa) => sum + (mesa.table?.capacity || 0),
                        0
                      )
                      // Usar efficiency do primeiro (todos compartilham a mesma)
                      const groupEfficiency = allMesas[0]?.efficiency
                      const totalPeople =
                        groupEfficiency?.people || firstAllocation.queue?.people || 0
                      const totalWaste = groupEfficiency?.waste || totalCapacity - totalPeople
                      const wasteLabel =
                        totalWaste > 0
                          ? `Desperdício de ${totalWaste} lugar${totalWaste !== 1 ? 'es' : ''}`
                          : 'Sem desperdício'

                      return (
                        <p
                          style={{
                            fontSize: 11,
                            color: '#6E7586',
                            margin: '0 0 8px 0',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <Armchair size={12} />
                          Mesa(s) Alocada(s) {allMesas.length > 1 ? `(${allMesas.length})` : ''} -
                          Total {totalCapacity} lugar{totalCapacity !== 1 ? 'es' : ''} -{' '}
                          {wasteLabel}
                        </p>
                      )
                    })()}

                    {/* Se houver apenas 1 mesa, mostrar em formato simples */}
                    {allMesas.length === 1 ? (
                      <div style={{marginBottom: 8}}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 8,
                          }}
                        >
                          <div style={{flex: 1}}>
                            <p style={{fontSize: 13, color: '#2A2D36', margin: 0, fontWeight: 600}}>
                              Mesa {allMesas[0].table?.number}
                            </p>
                            <p
                              style={{
                                fontSize: 11,
                                color: '#4377FF',
                                margin: '2px 0 4px 0',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                            >
                              <MapPin size={12} />
                              {allMesas[0].table?.environment}
                            </p>
                            <p style={{fontSize: 11, color: '#6E7586', margin: '2px 0 0 0'}}>
                              {allMesas[0].table?.capacity} lugares
                            </p>
                          </div>
                          {allMesas[0].efficiency && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: 4,
                              }}
                            >
                              <span
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: 4,
                                  backgroundColor: allMesas[0].efficiency.badgeColor,
                                  color: 'white',
                                  fontSize: 10,
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {allMesas[0].efficiency.badge}
                              </span>
                              <small style={{fontSize: 10, color: '#6E7586', textAlign: 'right'}}>
                                {allMesas[0].efficiency.wastePercentage}% desperdício
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Mostrar badge UMA VEZ para o grupo (não em cada mesa) */}
                        {allMesas[0]?.efficiency && (
                          <div
                            style={{
                              marginBottom: 12,
                              padding: 8,
                              backgroundColor: '#F5F7FA',
                              borderRadius: 6,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '4px 8px',
                                borderRadius: 4,
                                backgroundColor: allMesas[0].efficiency.badgeColor,
                                color: 'white',
                                fontSize: 10,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {allMesas[0].efficiency.badge}
                            </span>
                            <small style={{fontSize: 10, color: '#6E7586'}}>
                              {allMesas[0].efficiency.wastePercentage}% desperdício do grupo
                            </small>
                          </div>
                        )}

                        {/* Grid de mesas sem badges individuais */}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                            gap: 8,
                            marginBottom: 8,
                          }}
                        >
                          {allMesas.map((alloc) => (
                            <div
                              key={alloc.id}
                              style={{
                                padding: 8,
                                backgroundColor: '#FFF',
                                border: '1px solid #E0E4EA',
                                borderRadius: 6,
                                fontSize: 11,
                              }}
                            >
                              <p
                                style={{
                                  fontSize: 12,
                                  color: '#2A2D36',
                                  margin: '0 0 4px 0',
                                  fontWeight: 600,
                                }}
                              >
                                Mesa {alloc.table?.number}
                              </p>
                              <p
                                style={{
                                  fontSize: 10,
                                  color: '#4377FF',
                                  margin: '2px 0 4px 0',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                              >
                                <MapPin size={11} />
                                {alloc.table?.environment}
                              </p>
                              <p style={{fontSize: 10, color: '#6E7586', margin: '2px 0 0 0'}}>
                                {alloc.table?.capacity} lugares
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Método de alocação */}
                {firstAllocation.allocationMethod && (
                  <p
                    style={{
                      fontSize: 11,
                      color: '#6E7586',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Zap size={12} />
                    {firstAllocation.allocationMethod === 'automatic'
                      ? 'Alocação automática'
                      : 'Alocação manual'}
                  </p>
                )}

                {/* Notas */}
                {firstAllocation.notes && (
                  <p style={{fontSize: 11, color: '#6E7586', margin: 0, fontStyle: 'italic'}}>
                    {firstAllocation.notes}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export {AllocationsList}
